import { useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Upload, ScanLine, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { makeApi } from "@/lib/api";
import { DocScanner, PhotoInputs } from "@/components/scanner/DocScanner";

const okFile = (f) => f.type === "application/pdf" || f.type.startsWith("image/") || /\.pdf$/i.test(f.name);
const NOUN = { Ratecon: "ratecon", POD: "POD/BOL" };

// Admin upload for a load: drag & drop, file picker, or the page scanner.
export const AdminDocUpload = ({ loadId, type, loadNumber, compact, onDone }) => {
  const { getToken } = useAuth();
  const pickRef = useRef(null);
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [scanFiles, setScanFiles] = useState(null);
  const slug = type.toLowerCase();

  const send = async (files) => {
    const api = makeApi(getToken);
    const out = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("type", type);
      fd.append("file", f);
      const { data } = await api.post(`/loads/${loadId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      out.push(data);
    }
    return out;
  };

  const upload = async (list) => {
    const files = list.filter(okFile);
    if (files.length < list.length) toast.error("Use PDFs or images");
    if (!files.length) return;
    setBusy(true);
    try {
      const docs = await send(files);
      toast.success(files.length > 1 ? `${files.length} ${NOUN[type]}s uploaded` : `${NOUN[type]} uploaded`);
      onDone(docs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const saveScan = async (file) => {
    const docs = await send([file]);
    toast.success(`Scanned ${NOUN[type]} saved`);
    onDone(docs);
  };

  return (
    <div className="mt-4 space-y-2" data-testid={`admin-upload-${slug}`}>
      <div role="button" tabIndex={0} onClick={() => !busy && pickRef.current?.click()} onKeyDown={(e) => { if (e.key === "Enter" && !busy) pickRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); if (!busy) setOver(true); }} onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) upload([...e.dataTransfer.files]); }}
        className={`rounded-lg border-2 border-dashed text-center cursor-pointer transition-colors ${compact ? "p-3" : "p-5"} ${over ? "border-[#002FA7] bg-blue-50" : "border-slate-300 hover:border-[#002FA7]"}`}
        data-testid={`${slug}-dropzone`} data-dragging={over ? "true" : "false"}>
        <input ref={pickRef} type="file" multiple className="hidden" accept="application/pdf,image/*" onChange={(e) => { const fs = [...(e.target.files || [])]; e.target.value = ""; upload(fs); }} data-testid={`upload-${slug}-input`} />
        <div className="text-sm font-medium inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-slate-400" />}
          {busy ? "Uploading…" : over ? `Drop the ${NOUN[type]} here` : `${compact ? "Add more: drop" : "Drop"} ${NOUN[type]} files or click to browse`}
        </div>
        {!compact && <div className="text-xs text-slate-500 mt-0.5">PDF or image · several files at once is fine</div>}
      </div>
      <Button type="button" variant="outline" size="sm" className="w-full" disabled={busy} onClick={() => camRef.current?.click()} data-testid={`scan-${slug}-btn`}>
        <ScanLine className="w-4 h-4 mr-1.5" /> Scan {NOUN[type]} with camera
      </Button>
      <PhotoInputs cameraRef={camRef} galleryRef={galRef} onFiles={(files) => setScanFiles(files)} />
      <DocScanner open={!!scanFiles} initialFiles={scanFiles} loadNumber={loadNumber} prefix={type === "POD" ? "POD" : "Ratecon"} onClose={() => setScanFiles(null)} onSave={saveScan} />
    </div>
  );
};
